import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, X, ChevronDown, BookOpen, Video, HardDrive,
  Eye, ExternalLink, Download, Loader2, AlertTriangle,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Pagination from '../../components/ui/Pagination';
import { clientService } from '../../services';

// Shared chrome — matches client SessionsPage / admin pages.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)]';

const STAT_CHIP = {
  blue:    'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20',
  violet:  'bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-500/20',
  emerald: 'bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20',
};

// File-type badge palette (ring-inset for clean borders).
const FILE_TYPE_TONE = {
  pdf:  'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20',
  doc:  'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20',
  docx: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20',
  ppt:  'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-100 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20',
  pptx: 'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-100 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20',
  xls:  'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20',
  xlsx: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20',
};
const FILE_TYPE_FALLBACK = 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-100 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/20';

/**
 * Workbooks Page - Client
 * View all workbooks from organization's sessions
 */

const WorkbooksPage = () => {
  const navigate = useNavigate();
  const [workbooks, setWorkbooks] = useState([]);
  const [filteredWorkbooks, setFilteredWorkbooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSession, setSelectedSession] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchWorkbooks();
  }, []);

  const fetchWorkbooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clientService.getWorkbooks();
      console.log('Workbooks API response:', response);

      // Log debug info if available
      if (response.debug) {
        console.log('=== DEBUG INFO ===');
        console.log('Total sessions found:', response.debug.totalSessions);
        console.log('Skipped workbooks (no valid URL):', response.debug.skippedWorkbooks);
        console.log('Raw workbooks from DB:', response.debug.rawWorkbooks);
        console.log('==================');
      }

      if (response.success) {
        const allWorkbooks = response.data || [];
        console.log('Workbooks loaded:', allWorkbooks);
        setWorkbooks(allWorkbooks);
        setFilteredWorkbooks(allWorkbooks);
      } else {
        setError(response.error || 'Failed to load workbooks');
      }
    } catch (error) {
      console.error('Error fetching workbooks:', error);
      setError(error.message || 'An error occurred while loading workbooks');
    } finally {
      setLoading(false);
    }
  };

  // Filter workbooks
  useEffect(() => {
    let filtered = workbooks;

    // Filter by session
    if (selectedSession !== 'all') {
      filtered = filtered.filter((wb) => wb.sessionId === selectedSession);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (wb) =>
          (wb.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (wb.sessionName || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredWorkbooks(filtered);
    setCurrentPage(1);
  }, [searchQuery, selectedSession, workbooks]);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get unique sessions for filter
  const uniqueSessions = workbooks.reduce((acc, wb) => {
    const sessionId = wb.sessionId;
    const sessionName = wb.sessionName;
    if (sessionId && !acc.find(s => s.id === sessionId)) {
      acc.push({ id: sessionId, name: sessionName });
    }
    return acc;
  }, []);

  const totalSizeMB = (workbooks.reduce((acc, wb) => acc + (wb.size || 0), 0) / 1024 / 1024).toFixed(0);
  const paginated = filteredWorkbooks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const openWorkbook = (workbook) => {
    if (workbook.url && workbook.url.startsWith('http')) {
      const fileExt = workbook.url.split('.').pop()?.toLowerCase() || '';
      if (['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx'].includes(fileExt)) {
        window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(workbook.url)}&embedded=true`, '_blank');
      } else {
        window.open(workbook.url, '_blank');
      }
    }
  };

  const downloadWorkbook = async (workbook) => {
    if (!workbook.url || !workbook.url.startsWith('http')) return;
    try {
      const response = await fetch(workbook.url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = workbook.name || 'workbook';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(workbook.url, '_blank');
    }
  };

  const stats = [
    { label: 'Total Workbooks', value: workbooks.length, Icon: BookOpen, variant: 'blue', numCls: 'text-neutral-900 dark:text-neutral-50' },
    { label: 'Sessions', value: uniqueSessions.length, Icon: Video, variant: 'violet', numCls: 'text-neutral-900 dark:text-neutral-50' },
    { label: 'Total Size', value: `${totalSizeMB} MB`, Icon: HardDrive, variant: 'emerald', numCls: 'text-neutral-900 dark:text-neutral-50' },
  ];

  return (
    <DashboardLayout title="Workbooks">
      <div className="w-full px-4 lg:px-6 py-6 space-y-5 pb-20 sm:pb-6">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors group"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
          <span className="font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Workbooks</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">View all workbooks from your organization's sessions</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {stats.map(({ label, value, Icon, variant, numCls }) => (
            <div key={label} className={`${CARD_BASE} p-5`}>
              <div className="flex items-center gap-2.5 mb-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${STAT_CHIP[variant]}`}>
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
              </div>
              <span className={`text-3xl font-semibold tabular-nums tracking-tight leading-none ${numCls}`}>{value}</span>
            </div>
          ))}
        </div>

        {/* Workbooks Table Card */}
        <div className={`${CARD_BASE} overflow-hidden`}>
          {/* Search and Filters toolbar */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between p-4 border-b border-neutral-100 dark:border-neutral-800/70">
            <div className="relative flex-1 w-full sm:max-w-sm">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search workbooks…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-9 py-2.5 text-sm rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:bg-white dark:focus:bg-neutral-900 transition placeholder:text-neutral-400 text-neutral-900 dark:text-neutral-100"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <select
                  value={selectedSession}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  className="appearance-none w-full pl-3 pr-9 py-2.5 text-sm rounded-xl bg-neutral-50 dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500/30 cursor-pointer text-neutral-700 dark:text-neutral-200 min-w-[160px]"
                >
                  <option value="all">All Sessions</option>
                  {uniqueSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
              </div>
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 px-2.5 py-1 bg-neutral-50 dark:bg-neutral-800/70 rounded-full ring-1 ring-inset ring-neutral-100 dark:ring-neutral-700/60 whitespace-nowrap">
                {filteredWorkbooks.length} {filteredWorkbooks.length === 1 ? 'file' : 'files'}
              </span>
            </div>
          </div>

          {/* Table / States */}
          {loading ? (
            <div className="text-center py-16">
              <Loader2 size={28} strokeWidth={1.75} className="animate-spin mx-auto text-primary-500" />
              <p className="text-neutral-500 mt-4 text-sm">Loading workbooks…</p>
            </div>
          ) : error ? (
            <div className="text-center py-16 px-4">
              <span className="inline-flex w-14 h-14 rounded-2xl items-center justify-center bg-rose-50 text-rose-500 ring-1 ring-inset ring-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20 mb-4">
                <AlertTriangle size={22} strokeWidth={1.75} />
              </span>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Error loading workbooks</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">{error}</p>
              <Button variant="primary" size="sm" onClick={fetchWorkbooks}>
                Try Again
              </Button>
            </div>
          ) : filteredWorkbooks.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed min-w-[720px]">
                <colgroup>
                  <col className="w-[30%]" />
                  <col className="w-[26%]" />
                  <col className="w-[14%]" />
                  <col className="w-[8%]" />
                  <col className="w-[10%]" />
                  <col className="w-[12%]" />
                </colgroup>
                <thead>
                  <tr className="bg-neutral-50/70 dark:bg-neutral-900/60">
                    <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">File</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Session</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Trainer</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Size</th>
                    <th className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Date</th>
                    <th className="text-right py-3 px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 dark:text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((workbook, index, arr) => {
                    const isLast = index === arr.length - 1;
                    const ext = (workbook.name || '').split('.').pop()?.toLowerCase() || '';
                    const tone = FILE_TYPE_TONE[ext] || FILE_TYPE_FALLBACK;
                    const label = ext ? ext.toUpperCase() : 'FILE';
                    const disabled = workbook.hasValidUrl === false;

                    return (
                      <tr
                        key={workbook.id || workbook.url || `workbook-${index}`}
                        className={`hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors cursor-pointer ${
                          isLast ? '' : 'border-b border-neutral-100 dark:border-neutral-800/70'
                        }`}
                        onClick={() => openWorkbook(workbook)}
                      >
                        <td className="py-3 px-4 align-top whitespace-normal break-words">
                          <div className="flex items-start gap-3">
                            <div
                              aria-hidden="true"
                              className={`w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${tone}`}
                            >
                              {label}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 break-words">{workbook.name || 'Untitled Workbook'}</p>
                              {disabled && (
                                <span className="text-xs text-rose-500 mt-0.5 inline-block">URL Missing</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 align-top whitespace-normal break-words">
                          <div className="min-w-0">
                            <p className="text-sm text-neutral-700 dark:text-neutral-300 break-words">{workbook.sessionName}</p>
                            {workbook.sessionCode && (
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 font-mono">{workbook.sessionCode}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 align-top whitespace-normal break-words">
                          <span className="text-sm text-neutral-700 dark:text-neutral-300 break-words">{workbook.trainerName || '—'}</span>
                        </td>
                        <td className="py-3 px-4 align-top whitespace-nowrap">
                          <span className="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{formatFileSize(workbook.size)}</span>
                        </td>
                        <td className="py-3 px-4 align-top whitespace-nowrap">
                          <span className="text-sm text-neutral-700 dark:text-neutral-300">{formatDate(workbook.uploadedAt || workbook.sessionDate)}</span>
                        </td>
                        <td className="py-3 px-4 align-top text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => openWorkbook(workbook)}
                              className="p-1.5 rounded-lg text-neutral-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-neutral-400"
                              title="View Workbook"
                            >
                              <Eye size={16} strokeWidth={1.75} />
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(`/client/sessions/${workbook.sessionId}`)}
                              className="p-1.5 rounded-lg text-neutral-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors"
                              title="View Session"
                            >
                              <ExternalLink size={16} strokeWidth={1.75} />
                            </button>
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => downloadWorkbook(workbook)}
                              className="p-1.5 rounded-lg text-neutral-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-neutral-400"
                              title="Download"
                            >
                              <Download size={16} strokeWidth={1.75} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 px-4">
              <span className="inline-flex w-14 h-14 rounded-2xl items-center justify-center bg-neutral-100 text-neutral-400 ring-1 ring-inset ring-neutral-200 dark:bg-neutral-800/70 dark:text-neutral-500 dark:ring-neutral-700/60 mb-4">
                <BookOpen size={22} strokeWidth={1.75} />
              </span>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">No workbooks found</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {searchQuery || selectedSession !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Workbooks will appear here once they are uploaded to your sessions'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {filteredWorkbooks.length > 0 && (
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800/70">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredWorkbooks.length / itemsPerPage)}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredWorkbooks.length}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WorkbooksPage;
